/**
 * Parametry `:nazwa` podpowiadane w edytorze SQL — per miejsce, w którym tenant (lub ITE) pisze SQL.
 *
 * Listy zarezerwowanych parametrów to kopie kanonu z backendu (silnik wstawia je sam):
 *   - procedury kartotek  → apps/api/lib/record-hooks/reserved-params.ts (`RESERVED_RECORD_PARAM_NAMES`)
 *   - procedury procesów  → apps/api/lib/processes/procedure-executor.ts (`RESERVED_PARAM_NAMES`)
 *   - procedury dekretacji → apps/api/lib/documents/decree-procedure-executor.ts (`RESERVED_DECREE_PARAM_NAMES`)
 *   - widoki dodatkowe    → apps/api/lib/additional-views/reserved-params.ts (`RESERVED_VIEW_PARAM_NAMES`)
 *   - akcje SQL na listach → `SQL_PARAMS` w components/settings/ListActionsSettingsTab.tsx
 * Słowniki SQL (tenanta i globalne ITE) nie mają parametrów zarezerwowanych — tylko zadeklarowane.
 * Zmiana listy na backendzie = zmiana tutaj (podpowiedź, nie walidacja — rozjazd nie psuje zapisu).
 */

import type { TFunction } from 'i18next';

import type { SqlEditorCompletion } from '@/components/ui/LazySqlEditor';

export const RECORD_PROCEDURE_RESERVED_PARAMS = [
  'record_id',
  'record_type',
  'record_class',
  'lifecycle_event',
  'user_id',
  'company_id',
  'fields',
  'additional',
] as const;

/**
 * Procedury kartotek uruchamiane z przycisku SQL (zdarzenie `button`): zapytanie dostaje wyłącznie
 * dane systemowe — `:fields` i `:additional` są wtedy NULL, więc ich się nie podpowiada
 * (resztę danych rekordu zapytanie czyta z bazy, bo rekord jest już zapisany).
 */
export const RECORD_BUTTON_PROCEDURE_RESERVED_PARAMS = [
  'record_id',
  'record_type',
  'record_class',
  'lifecycle_event',
  'user_id',
  'company_id',
] as const satisfies readonly (typeof RECORD_PROCEDURE_RESERVED_PARAMS)[number][];

export const PROCESS_PROCEDURE_RESERVED_PARAMS = [
  'record_id',
  'process_id',
  'instance_id',
  'node_id',
  'route_key',
  'user_id',
  'target_module',
] as const;

export const DECREE_PROCEDURE_RESERVED_PARAMS = [
  'document_id',
  'company_id',
  'document_class',
  'user_id',
] as const;

export const ADDITIONAL_VIEW_RESERVED_PARAMS = ['record_id', 'user_id', 'company_id'] as const;

export const LIST_ACTION_RESERVED_PARAMS = [
  'record_id',
  'company_id',
  'user_id',
  'selected_ids',
] as const;

/** Wszystkie nazwy zarezerwowane (klucze opisów w i18n `sqlEditor.params.*`). */
export type ReservedSqlParamName =
  | (typeof RECORD_PROCEDURE_RESERVED_PARAMS)[number]
  | (typeof PROCESS_PROCEDURE_RESERVED_PARAMS)[number]
  | (typeof DECREE_PROCEDURE_RESERVED_PARAMS)[number]
  | (typeof ADDITIONAL_VIEW_RESERVED_PARAMS)[number]
  | (typeof LIST_ACTION_RESERVED_PARAMS)[number];

export interface DeclaredSqlParam {
  name: string;
  type?: string;
}

/**
 * Podpowiedzi dla edytora: najpierw zarezerwowane (z opisem), potem zadeklarowane w formularzu
 * (bez pustych nazw i bez dubli z zarezerwowanymi).
 */
export function buildSqlParamCompletions(
  t: TFunction,
  reserved: readonly ReservedSqlParamName[],
  declared: readonly DeclaredSqlParam[] = [],
): SqlEditorCompletion[] {
  const out: SqlEditorCompletion[] = reserved.map((name) => ({
    label: `:${name}`,
    detail: t(`sqlEditor.params.${name}`),
  }));
  const seen = new Set<string>(reserved);
  for (const p of declared) {
    const name = p.name.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push({
      label: `:${name}`,
      detail: p.type
        ? t('sqlEditor.declaredParamTyped', { type: p.type })
        : t('sqlEditor.declaredParam'),
    });
  }
  return out;
}
